// ============================================================
// AFFLICTIONS - weapons that inflict Conditions (v8.199.0).
//
// An AfflictionSpec is a pre-authored condition payload a weapon
// carries: a trigger (hit / wound / crit), an optional resist test
// (rolled through characteristicTests so DoS/DoF and the bonus×10
// statblock bases apply), and one or more condition effects with
// durations. The spec lives INLINE on the weapon - combat reads it off
// the projected Slot; palettes only copy specs around (never referenced
// at combat time), so a character exported with an afflictive weapon is
// self-contained forever.
//
// Pure, dice injected through engine/dice like every other combat
// module - a stray roll here would shift the E2E seeded sequence.
// ============================================================
import { rollDiceExpression } from './dice';
import { effectReaches, sideKey, type EffectAffects } from './effectScope';
import type { StackPolicy } from './stackPolicy';
import { resistTest, type ResistOutcome, type Vector } from './resistances';
import type { PlainTargetTestId, TestResult } from './characteristicTests';
import { mergeCondition, condType, buildCondCode, decodeCond } from './conditions';
import { isImmuneTo } from './conditionEligibility';
import { hasWS } from './weaponSpecials';
import type { WeaponEffectContext, WeaponEffectDetail, WeaponEffectResult } from './weaponEffects';

/** Which characteristics an affliction may demand a resist test of. */
export type AfflictionTestId = 'toughness' | 'agility' | 'strength' | 'willpower';

/** How long an inflicted condition lasts. */
/** `dof` = the victim's degrees of failure on the resist; `dos` = the attacker's degrees of success on the hit (v8.367.3). */
export type AfflictionDurationMode = 'permanent' | 'rounds' | 'dof' | 'dos' | 'dice';

export interface AfflictionEffect {
  /**
   * A condition code exactly as the picker would store it: a catalog
   * code ('S', 'B', 'P', 'Fi', 'Sn', 'Pn'…), a Fatigue level ('F2'),
   * or a scoped custom penalty ('Pen:dodge+movement=-10'). Durations are
   * NOT encoded here - they come from the fields below, so one effect
   * cannot carry two competing clocks.
   */
  code: string;
  durationMode: AfflictionDurationMode;
  /** Rounds, when mode is 'rounds'. */
  rounds?: number;
  /** Dice expression (e.g. '1d5'), when mode is 'dice'. */
  dice?: string;
}

export interface AfflictionSpec {
  /** Display name - 'Neurotoxin', chip label and log heading. */
  name: string;
  trigger: 'onHit' | 'onWound' | 'onCrit';
  /**
   * Absent/null = no resist; the affliction simply lands. `vector` names
   * the channel the resistance engine judges it by (v8.348.0) - a gas
   * affliction finds a Machine immune, a toxin one gives an Astartes the
   * Oolitic re-roll. Absent = no vector: nobody resists it specially.
   */
  test?: { characteristic: AfflictionTestId; modifier: number; vector?: Vector } | null;
  effects: AfflictionEffect[];
  /** Who it reaches, by the attacker's team (v8.367.4). Absent = anyone it hits. */
  affects?: EffectAffects;
  /** What it does to the same condition already worn (v8.367.4). Absent = the session's ruling. */
  stacking?: StackPolicy;
  /**
   * Grant a per-turn escape test that clears every condition this
   * affliction applied (the On-Fire banner machinery). The engine can
   * only honour it for codes the TURN_EFFECTS table knows - recorded
   * here so the editor can say so.
   */
  endsOnEscape?: boolean;
}

/** The 'agility' resist maps onto the dodge plain test - the same
    approximation Flame has always made for statblocks. */
const TEST_MAP: Record<AfflictionTestId, PlainTargetTestId> = {
  toughness: 'toughness',
  agility: 'dodge',
  strength: 'strength',
  willpower: 'willpower',
};

const TEST_LABEL: Record<AfflictionTestId, string> = {
  toughness: 'Toughness',
  agility: 'Agility',
  strength: 'Strength',
  willpower: 'Willpower',
};

/** Whether this attack's outcome satisfies the spec's trigger. */
export function afflictionTriggered(spec: AfflictionSpec, ctx: WeaponEffectContext): boolean {
  const landed = ctx.hits.some((h) => !h.ffBlocked);
  if (spec.trigger === 'onHit') return landed;
  if (spec.trigger === 'onWound') return ctx.dealtDamage;
  return ctx.hits.some((h) => !h.ffBlocked && h.critSev > 0);
}

/** The stored code for one effect, its duration resolved. Dice are rolled
    by the caller and passed in so this stays deterministic. */
function resolveEffectCode(effect: AfflictionEffect, dof: number, dos: number, rolledDice: number): string {
  const base = condType(effect.code);
  if (effect.durationMode === 'permanent') return effect.code;
  if (effect.durationMode === 'rounds') return buildCondCode(base, Math.max(0, effect.rounds ?? 0)) || effect.code;
  if (effect.durationMode === 'dof') return buildCondCode(base, Math.max(1, dof));
  if (effect.durationMode === 'dos') return buildCondCode(base, Math.max(1, dos));
  return buildCondCode(base, Math.max(1, rolledDice));
}

const ICON = '☣';

/** Log one line: the narrative note and its detail row carry the same text. */
function report(result: WeaponEffectResult, detail: WeaponEffectDetail): void {
  result.narrativeNotes.push(detail.result);
  result.effectDetails.push(detail);
}

/** A test line with its roll and target: "☣ Neurotoxin: FAIL Toughness test (71 vs 40...". */
function testLine(spec: AfflictionSpec, verdict: string, test: { roll: number; target: number }): string {
  return (
    ICON +
    ' ' +
    spec.name +
    ': ' +
    verdict +
    ' ' +
    TEST_LABEL[spec.test!.characteristic] +
    ' test (' +
    test.roll +
    ' vs ' +
    test.target
  );
}

/** A detail row for a rolled test. */
function testDetail(spec: AfflictionSpec, test: { roll: number; target: number }, success: boolean, text: string) {
  return { name: spec.name, icon: ICON, roll: test.roll, target: test.target, success, result: text };
}

/**
 * Judge the deciding test: a pass resists (null); a failure lands the
 * effects with its degrees of failure, never fewer than one.
 */
function judgeResist(spec: AfflictionSpec, test: TestResult, result: WeaponEffectResult): number | null {
  if (test.success) {
    report(result, testDetail(spec, test, true, testLine(spec, 'Pass', test) + ') - resists.'));
    return null;
  }
  const dof = Math.max(1, test.dof);
  report(result, testDetail(spec, test, false, testLine(spec, 'FAIL', test) + ', ' + dof + ' DoF)!'));
  return dof;
}

/** Log the failed first roll a re-roll replaced, so both show. */
function reportReroll(spec: AfflictionSpec, out: ResistOutcome, result: WeaponEffectResult): void {
  const f = out.firstTest!;
  const text =
    testLine(spec, 'FAIL', f) + ') - ' + out.resistance.rerollSources.join('/') + ' re-roll: ' + out.test!.roll;
  report(result, testDetail(spec, f, false, text));
}

/**
 * The victim's resist, when the spec offers one: the degrees of failure
 * the effects land with, or null when it was resisted or the victim is
 * immune. No test (or a Hexed weapon, v8.289.0, whose afflictions cannot
 * be resisted) lands with one.
 */
function resistAffliction(spec: AfflictionSpec, ctx: WeaponEffectContext, result: WeaponEffectResult): number | null {
  if (!spec.test || hasWS(ctx.specialRules, 'Hexed')) return 1;
  // Through the resistance engine (v8.348.0): an immune subject rolls
  // nothing, a re-roll appends one die and is shown, a bonus goes in.
  // Purgatus reaches the immune, as it does for conditions below.
  const out = resistTest(
    ctx.target as unknown as Record<string, unknown>,
    spec.test.vector ?? '',
    TEST_MAP[spec.test.characteristic],
    0,
    spec.test.modifier,
    { ignoreImmunity: hasWS(ctx.specialRules, 'Purgatus') },
  );
  if (out.immune) {
    const text = ICON + ' ' + spec.name + ': immune (' + out.resistance.immuneSources.join(', ') + ').';
    report(result, { name: spec.name, icon: ICON, success: true, result: text });
    return null;
  }
  if (out.rerolled) reportReroll(spec, out, result);
  return judgeResist(spec, out.test!, result);
}

/** Lay one effect on the victim (unless immune), its duration resolved - rolling its dice if it has them. */
function applyEffect(
  spec: AfflictionSpec,
  effect: AfflictionEffect,
  ctx: WeaponEffectContext,
  dof: number,
  result: WeaponEffectResult,
): void {
  // Immunity honoured at apply time, exactly like the picker and the
  // built-in handlers (conditionEligibility.ts module note).
  if (
    !hasWS(ctx.specialRules, 'Purgatus') &&
    isImmuneTo(ctx.target as unknown as Record<string, unknown>, condType(effect.code))
  ) {
    const text = ICON + ' ' + spec.name + ': immune to ' + (decodeCond(effect.code) || effect.code) + '.';
    report(result, { name: spec.name, icon: ICON, result: text });
    return;
  }
  const rolledDice = effect.durationMode === 'dice' && effect.dice ? rollDiceExpression(effect.dice).total : 0;
  const code = resolveEffectCode(effect, dof, ctx.dos, rolledDice);
  mergeCondition(ctx.newConds, code, spec.stacking ?? ctx.stacking);
  const text = ICON + ' ' + spec.name + ': ' + (decodeCond(code) || code) + ' applied!';
  report(result, { name: spec.name, icon: ICON, result: text, conditionApplied: code });
}

/** True when the spec has effects, its trigger fired, and it reaches this victim. */
function specFires(spec: AfflictionSpec, ctx: WeaponEffectContext): boolean {
  if (!spec.effects?.length || !afflictionTriggered(spec, ctx)) return false;
  // Who it is for (v8.367.4): a gas meant for the enemy leaves the ally coughing but unafflicted.
  return effectReaches(
    spec.affects,
    sideKey(ctx.attackerSideTag, ctx.attackerTeamTag),
    sideKey(ctx.target.sideTag, ctx.target.teamTag),
  );
}

/** One affliction: if it fires and is not resisted, every effect it carries. */
function processSpec(spec: AfflictionSpec, ctx: WeaponEffectContext, result: WeaponEffectResult): void {
  if (!specFires(spec, ctx)) return;
  const dof = resistAffliction(spec, ctx, result);
  if (dof === null) return;
  for (const effect of spec.effects) applyEffect(spec, effect, ctx, dof, result);
}

/**
 * Resolve every affliction the slot carries against this attack.
 * Called by useApplyHits AFTER processAllWeaponEffects, so affliction
 * dice always come after every weapon-special die - appended to the
 * seeded sequence rather than shuffled into it. Within a spec the resist
 * rolls first, then each effect's duration dice in order.
 */
export function processAfflictions(ctx: WeaponEffectContext): WeaponEffectResult {
  const result: WeaponEffectResult = { narrativeNotes: [], extraDamage: 0, effectDetails: [] };
  const specs = (ctx.slot as { afflictions?: AfflictionSpec[] }).afflictions;
  if (!specs?.length || ctx.targetKilled) return result;
  for (const spec of specs) processSpec(spec, ctx, result);
  return result;
}
